import { useQuery } from '@tanstack/react-query';
import { getHeadersWithUniqueVisitorId } from '../../Auth';
import { resolveBrandclubHostDomain } from '../domain';

interface UseConversationStartersOptions {
  /**
   * The stacklineSku of the product to fetch conversation starters for
   */
  stacklineSku?: string;
  /**
   * Axios instance for making API calls (used in landing-page)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios?: any;
  /**
   * Base URL for API requests (used in checkout-shop-app when axios is not available)
   * @example 'https://api.brandclub.com/api-rewards'
   */
  apiBaseUrl?: string;
  /**
   * Whether the query should be enabled
   * @default true when stacklineSku is provided
   */
  enabled?: boolean;
  source?: 'pages';
}

interface UseConversationStartersResult {
  questions: string[];
  isLoading: boolean;
  error: Error | null;
}

const fetchConversationStarters = async (
  stacklineSku: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios?: any,
  apiBaseUrl?: string,
  source?: 'pages',
): Promise<string[]> => {
  const body = JSON.stringify({ stacklineSku, ...(source ? { source } : {}) });

  if (axios) {
    // Landing-page path: use axios instance
    const response = await axios.post('/concierge/conversationStarters', {
      stacklineSku,
      ...(source ? { source } : {}),
    });
    return response.data?.questions || [];
  } else if (apiBaseUrl) {
    // Checkout-shop-app path: use fetch with apiBaseUrl
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversationStarters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });
    if (response.ok) {
      const data = await response.json();
      return data?.questions || [];
    }
    return [];
  } else {
    // Default path: use fetch with relative URL and auth headers
    const headers = await getHeadersWithUniqueVisitorId();
    const response = await fetch(`${resolveBrandclubHostDomain()}/api-rewards/concierge/conversationStarters`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body,
    });

    if (response.ok) {
      const data = await response.json();
      return data?.questions || [];
    }
    return [];
  }
};

/**
 * Hook to fetch product-specific conversation starters
 *
 * @example
 * // In landing-page (with axios)
 * const { questions, isLoading } = useConversationStarters({
 *   stacklineSku: 'ABC123',
 *   axios: axiosInstance,
 * });
 *
 * @example
 * // In checkout-shop-app (with apiBaseUrl)
 * const { questions, isLoading } = useConversationStarters({
 *   stacklineSku: 'ABC123',
 *   apiBaseUrl: 'https://api.brandclub.com/api-rewards',
 * });
 *
 * @example
 * // With default fetch (relative URL)
 * const { questions, isLoading } = useConversationStarters({
 *   stacklineSku: 'ABC123',
 * });
 */
export const useConversationStarters = ({
  stacklineSku,
  axios,
  apiBaseUrl,
  enabled = true,
  source,
}: UseConversationStartersOptions): UseConversationStartersResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conversationStarters', stacklineSku, apiBaseUrl, source],
    queryFn: () => fetchConversationStarters(stacklineSku!, axios, apiBaseUrl, source),
    enabled: enabled && !!stacklineSku,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return {
    questions: data || [],
    isLoading,
    error: error as Error | null,
  };
};
